/**
 * Cody — the applications assistant, a button next to Autopilot on the Monday Mirror.
 * Queue (proposals in Final-Checks-first order) · Jobs (switch, prompt, house rules, versions, test) ·
 * Documents (framework + reference) · People (who Cody DMs, confidence bar) · Top-ups (sales pipeline) · Channels · Log.
 * Cody reads, asks the right person on Slack when unsure, and acts on their word — every send is a human's decision or a rule.
 */
import { useState, useEffect, useCallback } from "react";
import { Bot, Loader2, Check, Pause, X, FileText, Upload, Trash2, Eye, History, FlaskConical, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  codyStatus, codySaveJob, codyVersions, codyRestore, codyUploadDoc, codyRemoveDoc, codyQueue, codyRuns,
  codyRunDetail, codyApprove, codyPark, codyDismiss, codyRun, codySaveSettings, codyPack, codyPackInstall,
} from "@/lib/api";
import { PeopleTab } from "./CodyPeople";
import { TopupsTab } from "./CodyTopups";
import { QueryRules, QueryDecision } from "./CodyQuery";
import { MistakeDecision } from "./CodyMistake";

const MODE_CLS = { off: "bg-slate-100 text-slate-600", propose: "bg-amber-100 text-amber-800", auto: "bg-emerald-100 text-emerald-800" };
const REPLY_JOBS = ["ir_reply_reader", "broker_query", "mistake_review"];
const STATUS_CLS = {
  proposed: "bg-amber-100 text-amber-800", applied: "bg-emerald-100 text-emerald-800", parked: "bg-slate-200 text-slate-700",
  dismissed: "bg-slate-100 text-slate-500", failed: "bg-rose-100 text-rose-800", test: "bg-sky-100 text-sky-800", skipped: "bg-slate-100 text-slate-500",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

export function CodyButton({ open: openProp, onOpenChange }) {
  const [openOwn, setOpenOwn] = useState(false);
  const open = openProp === undefined ? openOwn : openProp;
  const setOpen = onOpenChange || setOpenOwn;
  const [status, setStatus] = useState(null);
  const refresh = useCallback(() => codyStatus().then(setStatus).catch(() => {}), []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 60000); return () => clearInterval(t); }, [refresh]);
  const anyOn = status && Object.values(status.jobs || {}).some(j => j.mode !== "off");
  return (
    <>
      <Button size="sm" variant={anyOn ? "default" : "outline"} className="h-8 text-xs relative" onClick={() => setOpen(true)} data-testid="cody-btn">
        <Bot className="w-3.5 h-3.5 mr-1" /> Cody
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="cody-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Bot className="w-4 h-4 text-violet-600" /> Cody
              {status && <Badge variant="outline" className="text-[10px]">{status.region} board</Badge>}
              <span className="text-[11px] font-normal text-slate-500">reads what arrives · asks the right person on Slack when unsure · acts on their word</span>
            </DialogTitle>
          </DialogHeader>
          {open && <CodyTabs status={status} refresh={refresh} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function CodyTabs({ status, refresh }) {
  const [tab, setTab] = useState("queue");
  const tabs = [["queue", `Queue${status?.queue ? ` (${status.queue})` : ""}`], ["jobs", "Jobs"], ["people", "People"], ["topups", "Top-ups"], ["docs", "Documents"], ["channels", "Channels"], ["log", "Log"]];
  if (!status) return <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto my-6" />;
  return (
    <div className="space-y-3">
      <div className="flex gap-1 border-b">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cody-tab-${k}`}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition ${tab === k ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "queue" && <QueueTab refresh={refresh} />}
      {tab === "jobs" && <JobsTab status={status} refresh={refresh} />}
      {tab === "people" && <PeopleTab />}
      {tab === "topups" && <TopupsTab />}
      {tab === "docs" && <DocsTab status={status} refresh={refresh} />}
      {tab === "channels" && <ChannelsTab status={status} refresh={refresh} />}
      {tab === "log" && <LogTab status={status} />}
    </div>
  );
}

function Writes({ writes }) {
  if (!writes?.length) return null;
  const label = (w) => ({ note: "Monday note", stips: "stips saved", ir_column: "Information Request column rewritten", status: `status → ${w.label}`, stip_received: "ID stip ticked",
    reply_email: `reply email to ${w.to || "the broker"}`, ask_uw: "ask the underwriter on Slack",
    chase_email: `${w.step || "first"} stips chase email to ${(w.to || []).join(", ") || "the broker"}${w.reply_to_message_id ? " (in their thread)" : ""}`,
    chase_mark: `chase ${w.n} recorded` }[w.op] || w.op);
  return <div className="text-[10px] text-slate-500">Writes: {writes.map(label).join(" · ")}</div>;
}

function QueueTab({ refresh }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState({});
  const [lines, setLines] = useState({});
  const load = useCallback(() => codyQueue().then(setRows).catch(() => err(null, "Failed to load queue")), []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, fn, ok) => {
    setBusy(id);
    try { await fn(); toast.success(ok); load(); refresh(); } catch (e) { err(e, "Action failed"); } finally { setBusy(""); }
  };
  if (!rows) return <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto my-4" />;
  if (!rows.length) return <div className="text-xs text-slate-500 py-6 text-center" data-testid="cody-queue-empty">Nothing waiting. Proposals appear here in Final-Checks-first order when a job is set to Propose.</div>;
  return (
    <div className="space-y-2" data-testid="cody-queue">
      {rows.map(r => {
        const a = r.answer || {};
        return (
          <div key={r.run_id} className="border rounded-lg p-3 space-y-1.5" data-testid={`cody-queue-row-${r.run_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-slate-800 truncate">{r.item_name || r.item_id}</div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Badge variant="outline" className="text-[10px]">{r.status_at_run || "—"}</Badge>
                <Badge variant="outline" className={`text-[10px] border-0 ${STATUS_CLS[r.status]}`}>{r.status}</Badge>
                <span className="text-[10px] text-slate-400">{fmt(r.at)}</span>
              </div>
            </div>
            <div className="text-[11px] text-slate-600">{r.verdict}{r.check && !r.check.ok ? ` — ${r.check.reasons.join("; ")}` : ""}</div>
            {a.items?.length > 0 && (
              <ul className="text-[11px] text-slate-700 pl-1 space-y-0.5">
                {a.items.map((it, i) => <li key={i}><span className={it.status === "received" ? "text-emerald-700" : it.status === "missing" ? "text-rose-700" : "text-amber-700"}>{it.status}</span> · {it.asked} <span className="text-slate-400">— {it.evidence}</span></li>)}
              </ul>
            )}
            {r.job === "stips_chase" && a.outstanding_line && (
              <div className="rounded border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] text-sky-900" data-testid={`cody-queue-broker-line-${r.run_id}`}>
                <div className="font-medium mb-1">To the broker (edit before approving if needed):</div>
                <Textarea className="text-[11px] min-h-[48px] bg-white" value={lines[r.run_id] ?? a.outstanding_line} onChange={(e) => setLines(p => ({ ...p, [r.run_id]: e.target.value }))} data-testid={`cody-queue-broker-line-input-${r.run_id}`} />
                {a.outstanding?.length > 0 && <ul className="mt-1 list-disc pl-4">{a.outstanding.map((x, i) => <li key={i}>{x}</li>)}</ul>}
              </div>
            )}
            {(a.note || a.reply) && (
              <Textarea className="text-[11px] min-h-[56px]" value={notes[r.run_id] ?? (a.note || a.reply)} onChange={(e) => setNotes(p => ({ ...p, [r.run_id]: e.target.value }))} data-testid={`cody-queue-note-${r.run_id}`} />
            )}
            <Writes writes={r.planned_writes} />
            {r.slack?.to && <div className="text-[10px] text-violet-700">Asked {r.slack.to.name} on Slack{r.slack.to.reason ? ` (${r.slack.to.reason})` : ""} — their reply there counts too.</div>}
            {r.job === "broker_query" && (r.planned_writes || []).some(w => w.op === "ask_uw") && <QueryDecision run={r} onDone={() => { load(); refresh(); }} />}
            {r.job === "mistake_review" && (r.planned_writes || []).some(w => w.op === "ask_uw") && <MistakeDecision run={r} onDone={() => { load(); refresh(); }} />}
            <div className="flex gap-2">
              {!(["broker_query", "mistake_review"].includes(r.job) && (r.planned_writes || []).some(w => w.op === "ask_uw")) && <Button size="sm" className="h-7 text-xs" disabled={busy === r.run_id} onClick={() => act(r.run_id, () => codyApprove(r.run_id, notes[r.run_id], r.job === "stips_chase" && lines[r.run_id] ? { outstanding_line: lines[r.run_id] } : undefined), "Applied")} data-testid={`cody-approve-${r.run_id}`}>
                {busy === r.run_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Approve</>}
              </Button>}
              {r.status !== "parked" && <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === r.run_id} onClick={() => act(r.run_id, () => codyPark(r.run_id), "Parked")} data-testid={`cody-park-${r.run_id}`}><Pause className="w-3 h-3 mr-1" /> Park</Button>}
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" disabled={busy === r.run_id} onClick={() => act(r.run_id, () => codyDismiss(r.run_id), "Dismissed")} data-testid={`cody-dismiss-${r.run_id}`}><X className="w-3 h-3 mr-1" /> Dismiss</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function JobsTab({ status, refresh }) {
  return (
    <div className="space-y-2" data-testid="cody-jobs">
      <div className="text-[11px] text-slate-500">
        <b>Off</b> = nothing runs (Test still works) · <b>Propose</b> = Cody reads and puts the result in the queue for a click · <b>Auto</b> = Cody reads and writes the note, the outstanding list and the status itself, logged. Model: {status.model}.
      </div>
      {Object.values(status.jobs).map(j => <JobCard key={j.key} job={j} counts={status.counts_7d?.[j.key] || {}} refresh={refresh} settings={status.settings} />)}
    </div>
  );
}

function JobCard({ job, counts, refresh, settings }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ system: job.system, house_rules: job.house_rules });
  useEffect(() => { setForm({ system: job.system, house_rules: job.house_rules }); }, [job.version, job.system, job.house_rules]);
  const [saving, setSaving] = useState(false);
  const [vers, setVers] = useState(null);
  const [testSf, setTestSf] = useState("");
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const [ruleCount, setRuleCount] = useState(null);
  const [showReply, setShowReply] = useState(false);
  const [replyFrom, setReplyFrom] = useState("");
  const [replyText, setReplyText] = useState("");
  const usesReply = REPLY_JOBS.includes(job.key);
  const setMode = async (mode) => {
    const warn = job.key === "broker_query"
      ? `AUTO: for "${job.title}" Cody will send the rule-based replies (e.g. term over 28 weeks → no) to the broker itself, and DM the underwriter for the rest. Continue?`
      : job.key === "mistake_review"
      ? `"${job.title}" always DMs the on-shift underwriter on Slack before moving or replying to anything — Auto vs Propose just changes how fast Cody raises it. Continue?`
      : `AUTO: Cody will write the note, rewrite the Information Request column and move IR deals to UW - IR itself for "${job.title}" — unless its confidence is under the bar, when it asks a person. Continue?`;
    if (mode === "auto" && !window.confirm(warn)) return;
    try { await codySaveJob(job.key, { mode }); toast.success(`${job.title}: ${mode}`); refresh(); } catch (e) { err(e, "Save failed"); }
  };
  const save = async () => {
    setSaving(true);
    try { await codySaveJob(job.key, form); toast.success("Saved as a new version"); setEdit(false); refresh(); } catch (e) { err(e, "Save failed"); } finally { setSaving(false); }
  };
  const loadVers = async () => { try { setVers(await codyVersions(job.key)); } catch (e) { err(e, "Failed to load versions"); } };
  const restore = async (v) => { try { await codyRestore(job.key, v); toast.success(`Restored v${v}`); refresh(); setVers(null); } catch (e) { err(e, "Restore failed"); } };
  const test = async () => {
    if (!testSf.trim()) return;
    setTesting(true); setTestRes(null);
    try { setTestRes(await codyRun(job.key, testSf.trim(), true, { from: replyFrom, text: replyText })); } catch (e) { err(e, "Test failed"); } finally { setTesting(false); }
  };
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={`cody-job-${job.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">{job.title} <Badge variant="outline" className={`text-[10px] border-0 ${MODE_CLS[job.mode]}`}>{job.mode}</Badge>
            <span className="text-[10px] font-normal text-slate-400">v{job.version}{job.updated_by ? ` · ${job.updated_by} ${fmt(job.updated_at)}` : ""}</span></div>
          <div className="text-[11px] text-slate-500">{job.blurb}</div>
          <div className="text-[10px] text-slate-500">7 days: {Object.entries(counts).length ? Object.entries(counts).map(([s, n]) => `${n} ${s}`).join(" · ") : "no runs yet"}</div>
        </div>
        <Select value={job.mode} onValueChange={setMode}>
          <SelectTrigger className="h-7 text-xs w-40 shrink-0" data-testid={`cody-mode-${job.key}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Off</SelectItem>
            <SelectItem value="propose">Propose — queue for a click</SelectItem>
            <SelectItem value="auto">Auto — Cody writes it</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEdit(e => !e)} data-testid={`cody-edit-${job.key}`}><FileText className="w-3 h-3 mr-1" /> {edit ? "Close editor" : "Edit prompt"}</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => (vers ? setVers(null) : loadVers())} data-testid={`cody-versions-${job.key}`}><History className="w-3 h-3 mr-1" /> Versions</Button>
        <div className="flex items-center gap-1 ml-auto">
          <Input className="h-7 text-xs w-32" placeholder="SF-12345" value={testSf} onChange={(e) => setTestSf(e.target.value)} data-testid={`cody-test-sf-${job.key}`} />
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={testing || !testSf.trim()} onClick={test} data-testid={`cody-test-run-${job.key}`}>
            {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><FlaskConical className="w-3 h-3 mr-1" /> Test on this deal</>}
          </Button>
        </div>
      </div>
      {usesReply && (
        <div className="space-y-1">
          <button type="button" onClick={() => setShowReply(s => !s)} className="text-[10px] text-violet-600 hover:underline" data-testid={`cody-test-reply-toggle-${job.key}`}>
            {showReply ? "− hide test reply" : "+ paste a reply to test the classification against (optional)"}
          </button>
          {showReply && (
            <div className="space-y-1">
              <Input className="h-7 text-xs" placeholder="reply from (optional, e.g. broker@example.com)" value={replyFrom} onChange={(e) => setReplyFrom(e.target.value)} data-testid={`cody-test-reply-from-${job.key}`} />
              <Textarea className="text-[11px] min-h-[50px]" placeholder="paste the reply text Cody should read against this deal" value={replyText} onChange={(e) => setReplyText(e.target.value)} data-testid={`cody-test-reply-text-${job.key}`} />
            </div>
          )}
        </div>
      )}
      {edit && (
        <div className="space-y-2">
          <div><div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">System prompt</div>
            <Textarea className="text-[11px] min-h-[90px] font-mono" value={form.system} onChange={(e) => setForm(f => ({ ...f, system: e.target.value }))} data-testid={`cody-system-${job.key}`} /></div>
          <div><div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">House rules</div>
            <Textarea className="text-[11px] min-h-[120px] font-mono" value={form.house_rules} onChange={(e) => setForm(f => ({ ...f, house_rules: e.target.value }))} data-testid={`cody-rules-${job.key}`} /></div>
          <div className="text-[10px] text-slate-500">Answer shape (fixed, checked before anything is written): <code className="font-mono">{job.answer.slice(0, 160)}…</code></div>
          <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving} data-testid={`cody-save-${job.key}`}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save as new version"}</Button>
        </div>
      )}
      {vers && (
        <div className="border rounded-md p-2 space-y-1" data-testid={`cody-version-list-${job.key}`}>
          {!vers.length && <div className="text-[11px] text-slate-500">No saved versions yet — the defaults are in use.</div>}
          {vers.map(v => (
            <div key={v.version} className="flex items-center justify-between text-[11px]">
              <span>v{v.version} · {v.by} · {fmt(v.at)} · changed {v.changed?.join(", ")}</span>
              {v.version !== job.version && <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => restore(v.version)}>Restore</Button>}
            </div>
          ))}
        </div>
      )}
      {job.key === "broker_query" && (
        <details className="border rounded-md p-2 bg-slate-50" data-testid="cody-query-rules-box">
          <summary className="cursor-pointer text-[11px] font-medium text-slate-800">Query rules — the guardrails ({ruleCount ?? (settings?.query_rules || []).length}{ruleCount != null && ruleCount !== (settings?.query_rules || []).length ? ", unsaved" : ""})</summary>
          <div className="mt-2"><QueryRules rules={settings?.query_rules || []} onSaved={refresh} onDraftCount={setRuleCount} /></div>
        </details>
      )}
      {testRes && <RunDetail run={testRes} onClose={() => setTestRes(null)} />}
    </div>
  );
}

function RunDetail({ run, onClose }) {
  const [full, setFull] = useState(null);
  const a = run.answer || {};
  return (
    <div className="border rounded-md p-2 space-y-1.5 bg-slate-50" data-testid={`cody-run-detail-${run.run_id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-medium text-slate-800">{run.item_name || run.item_id} · <Badge variant="outline" className={`text-[10px] border-0 ${STATUS_CLS[run.status]}`}>{run.status}</Badge> <span className="text-slate-500">{run.verdict}</span></div>
        <div className="flex gap-1">
          {run.inputs === undefined && !full && <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => codyRunDetail(run.run_id).then(setFull).catch(() => err(null, "Failed"))}><Eye className="w-3 h-3 mr-1" /> Prompt</Button>}
          {onClose && <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={onClose}><X className="w-3 h-3" /></Button>}
        </div>
      </div>
      {run.check && <div className="text-[10px] text-slate-500">Checker: {run.check.ok ? "ok" : run.check.reasons.join("; ")} · {run.tokens || 0} tokens · {run.ms ? `${(run.ms / 1000).toFixed(1)} s` : ""}</div>}
      {a.items?.length > 0 && <ul className="text-[11px] pl-1 space-y-0.5">{a.items.map((it, i) => <li key={i}><b>{it.status}</b> · {it.asked} <span className="text-slate-400">— {it.evidence}</span></li>)}</ul>}
      {(a.note || a.reply) && <div className="text-[11px] whitespace-pre-wrap text-slate-700">{a.note || a.reply}</div>}
      {a.outstanding_line && <div className="text-[11px] text-amber-800">Broker line: {a.outstanding_line}</div>}
      {typeof a.is_mistake === "boolean" && (
        <div className="text-[11px] text-slate-700">
          <b>{a.is_mistake ? `Mistake (${a.kind})` : "Not a mistake"}</b>{a.explanation ? ` — ${a.explanation}` : ""}
          {a.is_mistake && a.suggested_status && <> · suggested status: <b>{a.suggested_status}</b></>}
          {typeof a.confidence === "number" && <span className="text-slate-400"> · {Math.round(a.confidence * 100)}% confidence</span>}
        </div>
      )}
      <Writes writes={run.planned_writes} />
      {(full?.prompt || run.prompt) && <pre className="text-[10px] whitespace-pre-wrap max-h-64 overflow-y-auto bg-white border rounded p-2">{full?.prompt || run.prompt}</pre>}
      {run.inputs && !full && <details className="text-[10px]"><summary className="cursor-pointer text-slate-500">What Cody was given</summary><pre className="whitespace-pre-wrap max-h-64 overflow-y-auto bg-white border rounded p-2">{JSON.stringify(run.inputs, null, 1)}</pre></details>}
    </div>
  );
}

function HousePack({ refresh }) {
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => codyPack().then(setPack).catch(() => setPack({ docs: [], house_rules: [], available: false })), []);
  useEffect(() => { load(); }, [load]);
  if (!pack) return null;
  const items = [...pack.docs.filter(d => d.present).map(d => ({ ...d, label: d.name, sub: `${d.kind}${d.job_keys?.length ? " · " + d.job_keys.join(", ") : ""} · ${(d.chars || 0).toLocaleString()} chars` })),
    ...pack.house_rules.map(h => ({ ...h, id: h.key, label: `${h.title} — house rules`, sub: `${h.words} words` }))];
  const pending = items.filter(i => i.state !== "installed");
  const cls = { installed: "bg-emerald-100 text-emerald-800", update: "bg-amber-100 text-amber-800", new: "bg-sky-100 text-sky-800" };
  const install = async () => {
    setBusy(true);
    try { const r = await codyPackInstall(); toast.success(r.done.length ? r.done.join(" · ") : "Already up to date"); setPack(r.status); refresh(); } catch (e) { err(e, "Install failed"); } finally { setBusy(false); }
  };
  return (
    <div className="border rounded-lg p-3 space-y-1.5 bg-slate-50" data-testid="cody-house-pack">
      <div className="flex items-center justify-between gap-2">
        <div><div className="text-xs font-semibold text-slate-800">House pack</div><div className="text-[10px] text-slate-500">Cody's framework, reference documents and per-job house rules, shipped with the portal. Installing writes new prompt versions (restorable) and keeps documents in step.</div></div>
        <Button size="sm" className="h-7 text-xs" disabled={busy || !pending.length} onClick={install} data-testid="cody-pack-install">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : pending.length ? `Install ${pending.length} update${pending.length === 1 ? "" : "s"}` : "Up to date"}</Button>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
        {items.map(i => (
          <div key={i.id} className="flex items-center justify-between gap-2" data-testid={`cody-pack-item-${i.id}`}>
            <span className="truncate text-slate-700">{i.label} <span className="text-slate-400">· {i.sub}</span></span>
            <Badge variant="outline" className={`text-[10px] border-0 shrink-0 ${cls[i.state] || ""}`}>{i.state}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocsTab({ status, refresh }) {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("framework");
  const [jobKey, setJobKey] = useState("");
  const [busy, setBusy] = useState(false);
  const docs = [...(status.docs?.framework || []), ...(status.docs?.reference || [])];
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try { const d = await codyUploadDoc(file, kind, jobKey ? [jobKey] : []); toast.success(d.status === "ready" ? `${d.name}: ${d.chars.toLocaleString()} characters read` : `${d.name}: ${d.error || d.status}`); setFile(null); refresh(); }
    catch (e) { err(e, "Upload failed"); } finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("Remove this document from Cody?")) return; try { await codyRemoveDoc(id); refresh(); } catch (e) { err(e, "Remove failed"); } };
  return (
    <div className="space-y-3" data-testid="cody-docs">
      <div className="text-[11px] text-slate-500">A <b>framework</b> document is read by every job on every run (the Cody framework). A <b>reference</b> document is read by the job you attach it to. PDF, DOCX, MD or TXT, up to 20 MB — text is extracted on upload (OCR for scans).</div>
      <div className="border rounded-lg p-3 flex flex-wrap items-center gap-2">
        <input type="file" accept=".pdf,.docx,.md,.txt" className="text-xs" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cody-doc-file" />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-7 text-xs w-36" data-testid="cody-doc-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="framework">Framework (all jobs)</SelectItem><SelectItem value="reference">Reference</SelectItem></SelectContent>
        </Select>
        {kind === "reference" && (
          <Select value={jobKey} onValueChange={setJobKey}>
            <SelectTrigger className="h-7 text-xs w-44" data-testid="cody-doc-job"><SelectValue placeholder="Attach to job…" /></SelectTrigger>
            <SelectContent>{Object.values(status.jobs).map(j => <SelectItem key={j.key} value={j.key}>{j.title}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Button size="sm" className="h-7 text-xs" disabled={!file || busy} onClick={upload} data-testid="cody-doc-upload">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> Upload</>}</Button>
      </div>
      <HousePack refresh={refresh} />
      {!docs.length && <div className="text-xs text-slate-500 text-center py-4" data-testid="cody-docs-empty">No documents yet — install the house pack above, or upload your own.</div>}
      {docs.map(d => (
        <div key={d.doc_id} className="border rounded-md p-2 flex items-center justify-between gap-2 text-[11px]" data-testid={`cody-doc-${d.doc_id}`}>
          <div className="min-w-0">
            <span className="font-medium text-slate-800">{d.name}</span> <Badge variant="outline" className="text-[10px]">{d.kind}</Badge>
            {d.job_keys?.length > 0 && <span className="text-slate-500"> · {d.job_keys.map(k => status.jobs[k]?.title || k).join(", ")}</span>}
            <div className="text-slate-500">{d.status === "ready" ? `${(d.chars || 0).toLocaleString()} characters` : d.status}{d.error ? ` — ${d.error}` : ""} · {d.uploaded_by} · {fmt(d.at)}</div>
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => remove(d.doc_id)} data-testid={`cody-doc-remove-${d.doc_id}`}><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
    </div>
  );
}

function ChannelsTab({ status, refresh }) {
  const c = status.channels || {};
  const yes = (v) => <span className={v ? "text-emerald-700" : "text-amber-700"}>{v ? "yes" : "no"}</span>;
  const announce = !!status.settings?.slack_announce;
  const toggleAnnounce = async () => {
    try { await codySaveSettings({ slack_announce: !announce }); toast.success(!announce ? "Queue posts to Slack on" : "Queue posts to Slack off"); refresh(); } catch (e) { err(e, "Save failed"); }
  };
  return (
    <div className="grid grid-cols-2 gap-3 text-[11px]" data-testid="cody-channels">
      <div className="border rounded-lg p-3 space-y-1"><div className="text-xs font-semibold text-slate-800">Monday</div><div>Board {c.monday?.board}. Reads items, notes and files; writes notes, the Information Request column, stips, and UW - IR under the same guard Autopilot uses.</div></div>
      <div className="border rounded-lg p-3 space-y-1"><div className="text-xs font-semibold text-slate-800">Email</div><div>{c.email?.mailbox || "mailbox not configured"}. Broker replies reach Cody when the Inbox Autopilot (or a person) posts them onto a deal. Nothing goes out from here — the Events handlers send.</div></div>
      <div className="border rounded-lg p-3 space-y-1"><div className="text-xs font-semibold text-slate-800 flex items-center gap-1"><Radio className="w-3 h-3" /> Slack</div>
        <div>Bot token {yes(c.slack?.bot_token)} · signing secret {yes(c.slack?.signing_secret)}{c.slack?.signing_secret_problem && <span className="text-amber-700"> ({c.slack.signing_secret_problem})</span>} · queue posts to {c.slack?.channel || "(work-group channel not set)"}:
          <Button size="sm" variant={announce ? "default" : "outline"} className="h-6 text-[11px] ml-1.5" onClick={toggleAnnounce} data-testid="cody-slack-announce">{announce ? "On" : "Off"}</Button></div>
        <div className="text-slate-500">Slack app → Event Subscriptions: <code>{window.location.origin}{c.slack?.events_url}</code> (app_mention, message.im) · Interactivity: <code>{window.location.origin}{c.slack?.actions_url}</code> · scopes app_mentions:read chat:write im:history im:write users:read users:read.email. Cody DMs the people on the <b>People</b> tab and acts on their replies; everyone can ask questions.</div>
        {c.slack?.last && <div className="text-slate-500">Last question: {fmt(c.slack.last.at)} · {c.slack.last.item_name}</div>}</div>
      <div className="border rounded-lg p-3 space-y-1"><div className="text-xs font-semibold text-slate-800">Sumsub</div><div>Identity check results on linked applicants run the Sumsub reader. {c.sumsub?.last ? `Last: ${fmt(c.sumsub.last.at)} · ${c.sumsub.last.item_name}` : "No results read yet."}</div></div>
    </div>
  );
}

function LogTab({ status }) {
  const [rows, setRows] = useState(null);
  const [job, setJob] = useState("all");
  const [st, setSt] = useState("all");
  const [open, setOpen] = useState(null);
  useEffect(() => { codyRuns(job === "all" ? "" : job, st === "all" ? "" : st).then(setRows).catch(() => err(null, "Failed to load log")); }, [job, st]);
  return (
    <div className="space-y-2" data-testid="cody-log">
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        <Select value={job} onValueChange={setJob}><SelectTrigger className="h-7 text-xs w-44" data-testid="cody-log-job"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All jobs</SelectItem>{Object.values(status.jobs).map(j => <SelectItem key={j.key} value={j.key}>{j.title}</SelectItem>)}</SelectContent></Select>
        <Select value={st} onValueChange={setSt}><SelectTrigger className="h-7 text-xs w-36" data-testid="cody-log-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All outcomes</SelectItem>{Object.keys(STATUS_CLS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <span className="ml-auto">{status.runs_30d} runs · {(status.tokens_30d || 0).toLocaleString()} tokens in 30 days</span>
      </div>
      {!rows ? <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto my-4" /> : !rows.length ? <div className="text-xs text-slate-500 text-center py-4">No runs yet.</div> : rows.map(r => (
        <div key={r.run_id} className="border rounded-md p-2 text-[11px]" data-testid={`cody-log-row-${r.run_id}`}>
          <div className="flex items-center justify-between gap-2 cursor-pointer" onClick={() => setOpen(open === r.run_id ? null : r.run_id)}>
            <div className="truncate"><span className="font-medium text-slate-800">{r.item_name || r.item_id || "—"}</span> · {status.jobs[r.job]?.title || r.job} · {r.trigger}{r.by && r.by !== "cody" ? ` · ${r.by}` : ""}</div>
            <div className="flex items-center gap-1.5 shrink-0"><Badge variant="outline" className={`text-[10px] border-0 ${STATUS_CLS[r.status] || ""}`}>{r.status}</Badge><span className="text-slate-400">{fmt(r.at)}</span></div>
          </div>
          <div className="text-slate-500 truncate">{r.verdict}</div>
          {open === r.run_id && <RunDetail run={r} />}
        </div>
      ))}
    </div>
  );
}
